import { onMounted, onUnmounted, ref, type Ref } from 'vue';
import { type OrthographicCamera } from 'three';
import { stop, stopAll, taggedLogger, type StopHandle } from '@/util';
import {
    createSceneRuntime,
    type SceneRuntime,
    type SceneRuntimeOptions,
} from '@/lib/threeScene/runtime/createSceneRuntime';
import { type SceneContext } from '@/lib/threeScene/SceneContext';

const log = taggedLogger('3D');

export type SceneSetupContext<TSceneContext, TCamera> = {
    canvas: HTMLCanvasElement;
    container: HTMLElement;
    sceneContext: TSceneContext;
    camera: TCamera;
    runtime: SceneRuntime;
};

export type SceneDefinition<
    TSceneContext extends SceneContext,
    TCamera extends OrthographicCamera,
> = {
    /** Build the Three.js camera. Called once on mount. */
    camera: () => TCamera;
    /** Build the scene context (Scene + node maps). Called once on mount. */
    sceneContext: (camera: TCamera) => TSceneContext;
    /**
     * Wire up reactive watchers, controls, scene-content updaters, and
     * interaction handlers. Called once after camera + sceneContext + runtime
     * are constructed. The returned StopHandle is invoked on unmount, before
     * the runtime is torn down.
     */
    setup: (ctx: SceneSetupContext<TSceneContext, TCamera>) => StopHandle;
    /** Optional overrides for the runtime (renderer params, debounce). */
    runtime?: Partial<Omit<SceneRuntimeOptions, 'canvas' | 'container' | 'scene' | 'camera'>>;
};

export type SceneRefs = {
    containerRef: Ref<HTMLDivElement | undefined>;
    canvasRef: Ref<HTMLCanvasElement | undefined>;
};

/**
 * Vue composable that mounts a Three.js scene to a `<canvas>` and tears it
 * down on unmount. Returns refs to bind in the component's template.
 *
 * On mount: builds camera → scene context → scene runtime → runs `setup`.
 * On unmount: invokes the StopHandle returned by `setup`, then disposes the
 * runtime (animation loop, resize observer, renderer).
 *
 * Use this in place of hand-rolling `onMounted`/`onUnmounted` + ref management
 * + `createSceneRuntime` + `stopAll` in every viewport component.
 */
export function useScene<
    TSceneContext extends SceneContext,
    TCamera extends OrthographicCamera,
>(definition: SceneDefinition<TSceneContext, TCamera>): SceneRefs {
    const containerRef = ref<HTMLDivElement>();
    const canvasRef = ref<HTMLCanvasElement>();
    let teardown: StopHandle | null = null;

    onMounted(() => {
        const canvas = canvasRef.value;
        const container = containerRef.value;
        if (!canvas || !container) {
            throw Error(
                'useScene: canvas/container ref not bound. Ensure your template has ' +
                    '<div ref="containerRef"><canvas ref="canvasRef" /></div>.',
            );
        }

        const camera = definition.camera();
        const sceneContext = definition.sceneContext(camera);

        const runtime = createSceneRuntime({
            container,
            canvas,
            scene: sceneContext.scene,
            camera,
            ...definition.runtime,
        });

        // The runtime owns the WebGLRenderer, animation loop, and resize observer
        // by the time createSceneRuntime returns. If setup() throws (e.g. an
        // attachRenderingState watcher init fails, an interaction handler errors
        // on a missing DOM element), we must dispose the runtime — otherwise
        // teardown stays null and onUnmounted's stop(null) is a no-op,
        // leaking GPU resources for the lifetime of the page.
        let stopSetup: StopHandle;
        try {
            stopSetup = definition.setup({ canvas, container, sceneContext, camera, runtime });
        } catch (error) {
            runtime.stop();
            throw error;
        }

        teardown = stopAll(stopSetup, runtime.stop);
    });

    onUnmounted(() => {
        // Swallow errors during teardown so a failed stop-handle in one part of
        // the scene doesn't prevent the rest from cleaning up, and doesn't
        // surface as an unhandled exception during component unmount.
        try {
            stop(teardown);
        } catch (error) {
            log.error('Error while tearing down scene: %s', error);
        }
        teardown = null;
    });

    return { containerRef, canvasRef };
}
